import { useCallback, useEffect, useMemo, useState } from "react";
import moment from "moment-timezone";
import { TimezoneOption } from "@core/features/timezones";
import {
  TimeslotId,
  Timeslot,
  useContiguousTimeslots,
  makeTimeslotsForRange,
  getLocalDateRangeFromUtcDates,
  formatDateHeaders,
  HeaderFormat,
} from "@core/features/timeslots";
import type { SchedulingMode } from "@core/features/actions";
import {
  SchedulingOutput,
  ProcessedSelectableOption,
} from "../scheduling.types";
import { useTimeslotSelection } from "./useTimeslotSelection";
import { getSelectedRepeatingOptionsFromSlots } from "../utils";

export type Args = {
  /** Array of UTC date strings in ISO format */
  utcDates: string[];
  /** Previously saved timeslot IDs */
  savedTimeSlots: TimeslotId[];
  /** User's selected timezone */
  selectedTimezone: TimezoneOption;
  /** Selection mode */
  mode: SchedulingMode;
  /** Grid start hour (e.g., 6 for 6 AM) */
  startHour: number;
  /** Grid end hour (e.g., 23 for 11 PM) */
  endHour: number;
  /** For 'freeform' mode: minimum total hours required */
  minRequiredHours: number;
  /** For 'freeform' mode: minimum contiguous block duration */
  minContiguousDuration: number;
  /** For 'select-options' and 'repeating' modes: available options in UTC (processed with required id/day) */
  selectableOptions: ProcessedSelectableOption[];
  /** For 'select-options' mode: number of options user must select */
  requiredSelections: number;
  /**
   * For 'select-options' and 'repeating' modes: maximum number of selections allowed.
   * For repeating mode, this counts unique time ranges (not individual options).
   * Defaults to 1 for repeating mode, unlimited for select-options.
   */
  maxSelections?: number;
  /** Header format: 'date' shows "Jan 14 PST", 'weekday' shows "Mon" */
  headerFormat: HeaderFormat;
};

export function useScheduling({
  utcDates,
  savedTimeSlots,
  selectedTimezone,
  mode,
  startHour,
  endHour,
  minRequiredHours,
  minContiguousDuration,
  selectableOptions,
  requiredSelections,
  maxSelections,
  headerFormat,
}: Args) {
  // Default maxSelections: 1 for repeating/repeating-dropdown mode, unlimited for select-options
  const effectiveMaxSelections =
    maxSelections ??
    (mode === "repeating" || mode === "repeating-dropdown" ? 1 : Infinity);
  // Track selected option IDs for select-options and repeating modes
  const [selectedOptionIds, setSelectedOptionIds] = useState<string[]>([]);

  // Check if we're in an options-based mode
  const isOptionsMode =
    mode === "select-options" ||
    mode === "repeating" ||
    mode === "repeating-dropdown";

  // For options modes, compute the local dates that options fall on
  // This is the most accurate way to determine which columns to show
  const optionLocalDates = useMemo(() => {
    if (
      !selectedTimezone?.name ||
      !isOptionsMode ||
      !selectableOptions.length
    ) {
      return [];
    }

    // For each option, compute the local date it falls on
    const datesSet = new Set<string>();
    for (const option of selectableOptions) {
      // Create a UTC moment for the option's start time
      const utcMoment = moment
        .utc(option.date)
        .hour(option.startHour)
        .minute(option.startMinute);
      // Convert to local timezone
      const localMoment = utcMoment.clone().tz(selectedTimezone.name);
      // Get the local date in ISO format
      const localDate = localMoment.format("YYYY-MM-DD");
      datesSet.add(localDate);
    }

    return [...datesSet].sort();
  }, [selectedTimezone, mode, selectableOptions]);

  // Calculate local dates from UTC dates (for freeform mode)
  const { localDates: freeformLocalDates } = useMemo(() => {
    if (!selectedTimezone?.name || !utcDates?.length) {
      return { localDates: [], columnCount: 0 };
    }

    return getLocalDateRangeFromUtcDates({
      utcDates,
      timezone: selectedTimezone,
      startHour,
      endHour,
    });
  }, [utcDates, selectedTimezone, startHour, endHour]);

  // Use option-derived local dates for options modes, freeform dates otherwise
  const allLocalDates = useMemo(() => {
    if (isOptionsMode) {
      return optionLocalDates;
    }
    return freeformLocalDates;
  }, [isOptionsMode, optionLocalDates, freeformLocalDates]);

  // Generate ALL time slots (needed to determine which options are visible)
  const allTimeSlots = useMemo(() => {
    if (!selectedTimezone?.name || !allLocalDates?.length) return [];

    return makeTimeslotsForRange({
      startHour,
      endHour,
      timezone: selectedTimezone,
      localDates: allLocalDates,
    });
  }, [selectedTimezone, allLocalDates, startHour, endHour]);

  // Helper to check if a slot matches an option's date/day
  const slotMatchesOptionDate = useCallback(
    (slot: Timeslot, option: ProcessedSelectableOption): boolean => {
      // If option has a specific date, match by date
      if (option.date) {
        // Use moment-timezone to parse UTC dateString and format as YYYY-MM-DD
        const slotDate = moment
          .utc(slot.utc.start.dateString)
          .format("YYYY-MM-DD");
        return slotDate === option.date;
      }
      // Otherwise match by day of week
      return slot.utc.start.dayIndex === option.day;
    },
    []
  );

  // Filter selectable options to only include those fully visible within the grid
  // Options that would be cut off by the grid boundaries are excluded
  // Note: repeating-dropdown mode doesn't use a grid, so all options are visible
  const visibleSelectableOptions = useMemo(() => {
    if (!isOptionsMode) {
      return selectableOptions;
    }

    // For repeating-dropdown mode, don't filter - there's no grid to fit within
    if (mode === "repeating-dropdown") {
      return selectableOptions;
    }

    // An option is visible if ALL its expected slots exist in the allTimeSlots array
    return selectableOptions.filter((option) => {
      // Calculate how many 30-min slots this option should have
      const optionDurationMinutes =
        option.endHour * 60 +
        option.endMinute -
        (option.startHour * 60 + option.startMinute);
      const expectedSlotCount = optionDurationMinutes / 30;

      // Count how many slots actually match this option
      const matchingSlots = allTimeSlots.filter((slot) => {
        const slotHour = slot.utc.start.hour;
        const slotMinute = slot.utc.start.minute;

        const slotTime = slotHour * 60 + slotMinute;
        const optStartTime = option.startHour * 60 + option.startMinute;
        const optEndTime = option.endHour * 60 + option.endMinute;

        return (
          slotMatchesOptionDate(slot, option) &&
          slotTime >= optStartTime &&
          slotTime < optEndTime
        );
      });

      // Option is visible only if all expected slots are present
      return matchingSlots.length === expectedSlotCount;
    });
  }, [selectableOptions, mode, allTimeSlots, slotMatchesOptionDate]);

  // For options modes, determine which local dates have visible options
  // This filters out date columns that would be empty (if an option was cut off)
  const localDatesWithOptions = useMemo(() => {
    if (!isOptionsMode) {
      return new Set(allLocalDates);
    }

    if (!selectedTimezone?.name) {
      return new Set<string>();
    }

    // Get all local dates that have at least one visible option
    const datesSet = new Set<string>();
    for (const option of visibleSelectableOptions) {
      // Compute the local date for this option
      const utcMoment = moment
        .utc(option.date)
        .hour(option.startHour)
        .minute(option.startMinute);
      const localMoment = utcMoment.clone().tz(selectedTimezone.name);
      const localDate = localMoment.format("YYYY-MM-DD");
      datesSet.add(localDate);
    }
    return datesSet;
  }, [mode, allLocalDates, visibleSelectableOptions, selectedTimezone]);

  // Filter local dates to only those with visible options (for options modes)
  const localDates = useMemo(() => {
    if (!isOptionsMode) {
      return allLocalDates;
    }
    return allLocalDates.filter((date) => localDatesWithOptions.has(date));
  }, [isOptionsMode, allLocalDates, localDatesWithOptions]);

  // Regenerate time slots using only filtered local dates
  // This ensures each time row has exactly one slot per column
  const timeSlots = useMemo(() => {
    if (!selectedTimezone?.name || !localDates?.length) return [];

    return makeTimeslotsForRange({
      startHour,
      endHour,
      timezone: selectedTimezone,
      localDates,
    });
  }, [selectedTimezone, localDates, startHour, endHour]);

  // Format headers based on filtered local dates
  const headers = useMemo(() => {
    if (!selectedTimezone?.name || !localDates.length) {
      return [];
    }

    return formatDateHeaders(localDates, selectedTimezone, headerFormat);
  }, [localDates, selectedTimezone, headerFormat]);

  // Column count based on filtered local dates
  const columnCount = localDates.length;

  // Hook to manage time slot selection state and callbacks
  const {
    selectedTimeSlots,
    setSelectedTimeSlots,
    handleMouseDown: baseHandleMouseDown,
    handleMouseOver: baseHandleMouseOver,
    handleTouchStart: baseHandleTouchStart,
    handleTouchMove,
    handleTouchEnd: baseHandleTouchEnd,
  } = useTimeslotSelection({
    allTimeSlots: timeSlots,
    savedTimeSlots,
  });

  // Find which selectable option contains a given slot (for select-options, repeating, and repeating-dropdown modes)
  // Options are defined with UTC date/day and UTC hours
  const findOptionForSlot = useCallback(
    (slot: Timeslot): ProcessedSelectableOption | undefined => {
      if (!isOptionsMode || !visibleSelectableOptions.length) {
        return undefined;
      }

      return visibleSelectableOptions.find((opt) => {
        // Use UTC time since options are defined in UTC
        const slotHour = slot.utc.start.hour;
        const slotMinute = slot.utc.start.minute;

        // Check if slot falls within this option's time range on the same date/day
        const slotTime = slotHour * 60 + slotMinute;
        const optStartTime = opt.startHour * 60 + opt.startMinute;
        const optEndTime = opt.endHour * 60 + opt.endMinute;

        return (
          slotMatchesOptionDate(slot, opt) &&
          slotTime >= optStartTime &&
          slotTime < optEndTime
        );
      });
    },
    [isOptionsMode, visibleSelectableOptions, slotMatchesOptionDate]
  );

  // Find all options with the same time range (for repeating mode)
  const findOptionsWithSameTime = useCallback(
    (option: ProcessedSelectableOption): ProcessedSelectableOption[] => {
      return visibleSelectableOptions.filter(
        (opt) =>
          opt.startHour === option.startHour &&
          opt.startMinute === option.startMinute &&
          opt.endHour === option.endHour &&
          opt.endMinute === option.endMinute
      );
    },
    [visibleSelectableOptions]
  );

  // Count unique time ranges in selected options (for repeating mode max check)
  const countSelectedTimeRanges = useCallback((): number => {
    const uniqueTimeRanges = new Set<string>();
    for (const optionId of selectedOptionIds) {
      const option = visibleSelectableOptions.find((o) => o.id === optionId);
      if (option) {
        const timeKey = `${option.startHour}:${option.startMinute}-${option.endHour}:${option.endMinute}`;
        uniqueTimeRanges.add(timeKey);
      }
    }
    return uniqueTimeRanges.size;
  }, [selectedOptionIds, visibleSelectableOptions]);

  // Get all slots that belong to a given option
  // Options are defined with UTC date/day and UTC hours
  const getSlotsForOption = useCallback(
    (option: ProcessedSelectableOption): Timeslot[] => {
      return timeSlots.filter((slot) => {
        // Use UTC time since options are defined in UTC
        const slotHour = slot.utc.start.hour;
        const slotMinute = slot.utc.start.minute;

        const slotTime = slotHour * 60 + slotMinute;
        const optStartTime = option.startHour * 60 + option.startMinute;
        const optEndTime = option.endHour * 60 + option.endMinute;

        return (
          slotMatchesOptionDate(slot, option) &&
          slotTime >= optStartTime &&
          slotTime < optEndTime
        );
      });
    },
    [timeSlots, slotMatchesOptionDate]
  );

  // Initialize selectedOptionIds from selectedTimeSlots when loading saved data
  // Uses the shared utility for repeating mode selection logic
  useEffect(() => {
    if (
      isOptionsMode &&
      selectedTimeSlots.length > 0 &&
      selectedOptionIds.length === 0 &&
      visibleSelectableOptions.length > 0
    ) {
      // Get the slot IDs from selected time slots
      const savedSlotIds = selectedTimeSlots.map((s) => s.id);

      // Use shared utility to find matching options
      const matchingOptions = getSelectedRepeatingOptionsFromSlots(
        visibleSelectableOptions,
        savedSlotIds
      );

      if (matchingOptions.length > 0) {
        const optionIds = matchingOptions
          .map((opt) => opt.id)
          .filter((id): id is string => id !== undefined);
        setSelectedOptionIds(optionIds);
      }
    }
  }, [
    mode,
    selectedTimeSlots,
    selectedOptionIds.length,
    visibleSelectableOptions,
  ]);

  // Mode-aware mouse down handler
  const handleMouseDown = useCallback(
    (e: React.MouseEvent, slot: Timeslot) => {
      if (mode === "select-options") {
        // Find the option containing this slot
        const option = findOptionForSlot(slot);
        if (!option) return;

        // Toggle the entire option
        const isSelected = selectedOptionIds.includes(option.id);
        if (isSelected) {
          setSelectedOptionIds((prev) => prev.filter((id) => id !== option.id));
          const optionSlots = getSlotsForOption(option);
          setSelectedTimeSlots((prev) =>
            prev.filter((s) => !optionSlots.some((os) => os.id === s.id))
          );
        } else {
          // Check if we've reached max selections
          if (selectedOptionIds.length >= effectiveMaxSelections) {
            return; // Don't allow more selections
          }
          setSelectedOptionIds((prev) => [...prev, option.id]);
          const optionSlots = getSlotsForOption(option);
          setSelectedTimeSlots((prev) => {
            const newSlots = optionSlots.filter(
              (os) => !prev.some((s) => s.id === os.id)
            );
            return [...prev, ...newSlots];
          });
        }
      } else if (mode === "repeating" || mode === "repeating-dropdown") {
        // In repeating/repeating-dropdown mode, clicking an option selects all options with the same time range
        const option = findOptionForSlot(slot);
        if (!option) return;

        // Find all options with the same time range across all days
        const matchingOptions = findOptionsWithSameTime(option);
        const matchingOptionIds = matchingOptions.map((o) => o.id);

        // Check if any of the matching options are already selected
        const isSelected = matchingOptionIds.some((id) =>
          selectedOptionIds.includes(id)
        );

        if (isSelected) {
          // Deselect all matching options
          setSelectedOptionIds((prev) =>
            prev.filter((id) => !matchingOptionIds.includes(id))
          );
          // Remove slots for all matching options
          const allMatchingSlots = matchingOptions.flatMap((opt) =>
            getSlotsForOption(opt)
          );
          setSelectedTimeSlots((prev) =>
            prev.filter((s) => !allMatchingSlots.some((ms) => ms.id === s.id))
          );
        } else {
          // Check if we've reached max selections (count unique time ranges)
          const currentTimeRanges = countSelectedTimeRanges();
          if (currentTimeRanges >= effectiveMaxSelections) {
            // Replace current selection with new one (clear all and select new)
            const allMatchingSlots = matchingOptions.flatMap((opt) =>
              getSlotsForOption(opt)
            );
            setSelectedOptionIds(matchingOptionIds);
            setSelectedTimeSlots(allMatchingSlots);
          } else {
            // Select all matching options (add to existing)
            setSelectedOptionIds((prev) => [
              ...prev,
              ...matchingOptionIds.filter((id) => !prev.includes(id)),
            ]);
            // Add slots for all matching options
            const allMatchingSlots = matchingOptions.flatMap((opt) =>
              getSlotsForOption(opt)
            );
            setSelectedTimeSlots((prev) => {
              const newSlots = allMatchingSlots.filter(
                (ms) => !prev.some((s) => s.id === ms.id)
              );
              return [...prev, ...newSlots];
            });
          }
        }
      } else {
        // Freeform mode - use base handler
        baseHandleMouseDown(e, slot);
      }
    },
    [
      mode,
      findOptionForSlot,
      findOptionsWithSameTime,
      selectedOptionIds,
      getSlotsForOption,
      setSelectedTimeSlots,
      setSelectedOptionIds,
      baseHandleMouseDown,
      effectiveMaxSelections,
      countSelectedTimeRanges,
    ]
  );

  // Mode-aware mouse over handler
  const handleMouseOver = useCallback(
    (e: React.MouseEvent, slot: Timeslot) => {
      if (isOptionsMode) {
        // No drag selection in options modes
        return;
      } else {
        baseHandleMouseOver(e, slot);
      }
    },
    [isOptionsMode, baseHandleMouseOver]
  );

  // Mode-aware touch start handler
  const handleTouchStart = useCallback(
    (e: React.TouchEvent, slot: Timeslot) => {
      if (isOptionsMode) {
        // Convert to mouse event for option selection
        handleMouseDown(e as unknown as React.MouseEvent, slot);
      } else {
        baseHandleTouchStart(e, slot);
      }
    },
    [isOptionsMode, handleMouseDown, baseHandleTouchStart]
  );

  // Mode-aware touch end handler
  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (isOptionsMode) {
        // No special handling needed
        return;
      } else {
        baseHandleTouchEnd(e);
      }
    },
    [isOptionsMode, baseHandleTouchEnd]
  );

  // Toggle option selection by option ID (for repeating-dropdown mode)
  // This allows direct selection without needing a Timeslot
  const toggleOptionSelection = useCallback(
    (optionId: string) => {
      const option = visibleSelectableOptions.find((o) => o.id === optionId);
      if (!option) return;

      // Find all options with the same time range across all days
      const matchingOptions = findOptionsWithSameTime(option);
      const matchingOptionIds = matchingOptions.map((o) => o.id);

      // Check if any of the matching options are already selected
      const isSelected = matchingOptionIds.some((id) =>
        selectedOptionIds.includes(id)
      );

      if (isSelected) {
        // Deselect all matching options
        setSelectedOptionIds((prev) =>
          prev.filter((id) => !matchingOptionIds.includes(id))
        );
        // Remove slots for all matching options
        const allMatchingSlots = matchingOptions.flatMap((opt) =>
          getSlotsForOption(opt)
        );
        setSelectedTimeSlots((prev) =>
          prev.filter((s) => !allMatchingSlots.some((ms) => ms.id === s.id))
        );
      } else {
        // Check if we've reached max selections (count unique time ranges)
        const currentTimeRanges = countSelectedTimeRanges();
        if (currentTimeRanges >= effectiveMaxSelections) {
          // Replace current selection with new one (clear all and select new)
          const allMatchingSlots = matchingOptions.flatMap((opt) =>
            getSlotsForOption(opt)
          );
          setSelectedOptionIds(matchingOptionIds);
          setSelectedTimeSlots(allMatchingSlots);
        } else {
          // Select all matching options (add to existing)
          setSelectedOptionIds((prev) => [
            ...prev,
            ...matchingOptionIds.filter((id) => !prev.includes(id)),
          ]);
          // Add slots for all matching options
          const allMatchingSlots = matchingOptions.flatMap((opt) =>
            getSlotsForOption(opt)
          );
          setSelectedTimeSlots((prev) => {
            const newSlots = allMatchingSlots.filter(
              (ms) => !prev.some((s) => s.id === ms.id)
            );
            return [...prev, ...newSlots];
          });
        }
      }
    },
    [
      visibleSelectableOptions,
      findOptionsWithSameTime,
      selectedOptionIds,
      getSlotsForOption,
      setSelectedTimeSlots,
      effectiveMaxSelections,
      countSelectedTimeRanges,
    ]
  );

  // Manage contiguous slots and valid hours count
  const { contiguousTimeslots, validHoursCount } = useContiguousTimeslots(
    selectedTimeSlots,
    minContiguousDuration
  );

  // Check if requirements are met based on mode
  const requirementsMet = useMemo(() => {
    if (mode === "freeform") {
      return validHoursCount >= minRequiredHours;
    } else if (mode === "select-options") {
      return selectedOptionIds.length >= requiredSelections;
    } else if (mode === "repeating" || mode === "repeating-dropdown") {
      // In repeating/repeating-dropdown mode, at least one time slot group must be selected
      return selectedOptionIds.length > 0;
    }
    return false;
  }, [
    mode,
    validHoursCount,
    minRequiredHours,
    selectedOptionIds.length,
    requiredSelections,
  ]);

  // Get output based on mode
  // Note: slotIds are deduplicated because the same weekly slot ID (0-335)
  // can appear multiple times when options span multiple weeks
  const getOutput = useCallback((): SchedulingOutput => {
    const uniqueSlotIds = [...new Set(selectedTimeSlots.map((s) => s.id))];

    if (mode === "freeform") {
      return {
        slotIds: uniqueSlotIds,
        dates: utcDates,
        mode: "freeform",
      };
    } else if (mode === "select-options") {
      return {
        slotIds: uniqueSlotIds,
        dates: utcDates,
        mode: "select-options",
        selectedOptionIds,
      };
    } else {
      // Repeating mode - same structure as select-options
      return {
        slotIds: uniqueSlotIds,
        dates: utcDates,
        mode: "repeating",
        selectedOptionIds,
      };
    }
  }, [mode, selectedTimeSlots, utcDates, selectedOptionIds]);

  // Compute current selection count for options modes
  const currentSelectionCount = useMemo(() => {
    if (mode === "select-options") {
      return selectedOptionIds.length;
    } else if (mode === "repeating" || mode === "repeating-dropdown") {
      return countSelectedTimeRanges();
    }
    return 0;
  }, [mode, selectedOptionIds, countSelectedTimeRanges]);

  // Check if max selections reached
  const maxSelectionsReached = currentSelectionCount >= effectiveMaxSelections;

  return {
    // Timeslots (filtered for options modes to only show dates with options)
    timeSlots,
    selectedTimeSlots,

    // Event handlers
    handleMouseDown,
    handleMouseOver,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,

    // Contiguous slots
    contiguousTimeslots,
    validHoursCount,

    // Requirements
    requirementsMet,

    // Computed display values (filtered for options modes to only show dates with options)
    localDates,
    headers,
    columnCount,

    // Mode-specific state
    selectedOptionIds,

    // Selection limits
    maxSelections: effectiveMaxSelections,
    currentSelectionCount,
    maxSelectionsReached,

    // Output
    getOutput,

    // Selectable options for UI (filtered to only include fully visible options)
    selectableOptions: visibleSelectableOptions,

    // Direct option selection (for repeating-dropdown mode)
    toggleOptionSelection,
  };
}

export default useScheduling;
